import {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";

import {service as SurveyService} from "../Services/SurveyService";

import {ObjectUtils} from '../Utils/ObjectUtils';
import {usePermission} from "./PermissionHooks";
import {SET_TOOLBARS} from "../Redux/Actions/Types";

import {ProgramsViewPage} from '../Components/Pages/Programs/ViewPage';
import {ProgramsIndexPage} from '../Components/Pages/Programs/IndexPage';
import {FilesIndexPage} from '../Components/Pages/Files/FilesIndexPage';
import {ProgramsTrackingPage} from '../Components/Pages/Programs/TrackingPage';
import {ProjectsViewPage} from '../Components/Pages/Projects/ViewPage';
import {ContactsIndexPage} from '../Components/Pages/Contacts/IndexPage';
import {PermitsIndexPage} from '../Components/Pages/Permits/IndexPage';
import {ContextNodeActivityPage} from '../Components/Pages/Activity/ActivityPage';
import {ConservationActionsViewPage} from '../Components/Pages/ConservationActions/ViewPage';
import {EligibilityPage} from '../Components/Pages/ConservationActions/EligibilityPage';
import {PermitsPage} from '../Components/Pages/ConservationActions/PermitsPage';
import {DesignPage} from '../Components/Pages/ConservationActions/DesignPage';
import {AsBuiltPage} from '../Components/Pages/ConservationActions/AsBuiltPage';
import {EventsIndexPage} from "../Components/Pages/Events/IndexPage";
import {SampleEventViewPage} from "../Components/Pages/Events/ViewPage";
import {PhotoComparisonPage} from "../Components/Pages/Photos";


export const useTools = (props) => {
    const params = props?.match?.params;

    if (params?.sampleEventId) {
        return getSampleEventTools(params.sampleEventId);
    } else if (params?.conActId) {
        return getConActTools(params.conActId);
    } else if (params?.projId) {
        return getProjectTools(params.projId);
    } else if (params?.cProgId) {
        return getSubProgramTools(params.cProgId);
    } else if (params?.pProgId) {
        return getProgramTools(params.pProgId);
    } else {
        return getRootTools();
    }
};

export const useToolBuilder = (props) => {
    const params = props?.match?.params;
    const tools = useTools(props);
    const dispatch = useDispatch();

    return () => {
        let newContext = {
            isValid: true,
            component: null,
            key: null
        };

        let tool = null;
        if(tools && tools.length) {
            if(params?.tool) {
                tool = tools.find((x) => x.key.toLowerCase() === params.tool.toLowerCase());
            } else {
                //if tool param doesn't exist lets use the second to last assuming we have something like /{context}/{hardcodedTool}/{id}
                let tokens = window.location.pathname.split('/');
                if(tokens.length > 1) {
                    let param = tokens[tokens.length-2];
                    tool = tools.find((x) => x.key.toLowerCase() === param);
                }
            }
        }

        let toolbars = [];
        tools.forEach((x) => {
            toolbars.push({
                title: x.title,
                active: x.key === (tool ? tool.key : ""),
                key: x.key,
                display: x.display,
                disabled: x.disabled,
            });
        });

        if(tool) {
            newContext.component = tool.component;
            newContext.key = tool.key;
        }

        dispatch({type: SET_TOOLBARS, toolbars});

        return newContext;
    };
};

export const useToolBuilderOverride = (props) => {
    const toolbars = useSelector(state => state.contextState.toolbars);
    const [surveyTypes, setSurveyTypes] = useState([]);
    const dispatch = useDispatch();

    useEffect(() => {
        if (props.match.params.conActId) {
            Promise.all([
                SurveyService.GetPublishedSurveyTypeCodesByConservationActionId(props.match.params.conActId)
            ]).then(([surveyTypes]) => {
                setSurveyTypes(surveyTypes.result);
            });
        }
    }, [props.match.params.conActId]);

    useEffect(() => {
        updateConservationActionToolBars();
    }, [toolbars, surveyTypes]);

    const updateConservationActionToolBars = () => {
        if( toolbars.length ) {
            let ret = [];

            toolbars.forEach(toolbarItem => {
                if (toolbarItem.display) {
                    if (surveyTypes.find(survey => survey?.code === toolbarItem.key)) {
                        // clone the toolbar item so we don't modify
                        // the original object in the toolbars array
                        toolbarItem = {
                            ...toolbarItem,
                            disabled: false,
                        }
                    }
                }

                ret.push(toolbarItem);
            });

            if(!ObjectUtils.Compare(toolbars, ret)) {
                dispatch({type: SET_TOOLBARS, toolbars: ret});
            }
        }
    };
};

const getRootTools = () => {
    const permission = usePermission();

    return [
        {
            key: "programs",
            title: "Programs",
            component: ProgramsIndexPage,
            display: true,
            isDefault: true
        },
        {
            key: "contacts",
            title: "Contacts",
            component: ContactsIndexPage,
            display: permission('view_contacts'),
            isDefault: false
        },
        {
            key: "events",
            title: "Events",
            component: EventsIndexPage,
            display: permission('view_sample_events'),
            isDefault: false,
            disabled: false,
        },
        {
            key: "files",
            title: "Files",
            component: FilesIndexPage,
            display: permission('view_files_tool'),
            isDefault: false
        },
        {
            key: "activity",
            title: "Activity",
            component: ContextNodeActivityPage,
            display: permission('view_root_activity_feed_events'),
            isDefault: false
        }
    ];
};

const getProgramTools = (slug) => {
    const permission = usePermission();

    let ret = [
        {
            key: "summary",
            title: "Summary",
            component: ProgramsViewPage,
            display: true,
            isDefault: true,
            disabled: false,
        },
        {
            key: "contacts",
            title: "Contacts",
            component: ContactsIndexPage,
            display: permission('view_contacts'),
            isDefault: false,
            disabled: false,
        },
        {
            key: "tracking",
            title: "Tracking",
            component: ProgramsTrackingPage,
            display: true,
            isDefault: false,
            disabled: false,
        },
        {
            key: "events",
            title: "Events",
            component: EventsIndexPage,
            display: permission('view_sample_events'),
            isDefault: false,
            disabled: false,
        },
        {
            key: "files",
            title: "Files",
            component: FilesIndexPage,
            display: permission('view_files_tool'),
            isDefault: false,
            disabled: false,
        },
        {
            key: "permit",
            title: "Permits",
            component: PermitsIndexPage,
            display: permission('view_survey') && permission('view_permits'),
            isDefault: false,
            disabled: false,
        },
        {
            key: "activity",
            title: "Activity",
            component: ContextNodeActivityPage,
            display: permission('view_program_activity_feed_events'),
            isDefault: false,
            disabled: false,
        }
    ];

    // unsaved models only have the summary tool
    if(slug === 'new') {
        return [ret[0]]
    }

    return ret;
};

const getSubProgramTools = (slug) => {
    return getProgramTools(slug).filter((x) => x.key !== "tracking");
};

const getProjectTools = (slug) => {
    const permission = usePermission();

    let ret = [
        {
            key: "summary",
            title: "Summary",
            component: ProjectsViewPage,
            display: true,
            isDefault: true,
            disabled: false,
        },
        {
            key: "contacts",
            title: "Contacts",
            component: ContactsIndexPage,
            display: permission('view_contacts'),
            isDefault: false,
            disabled: false,
        },
        {
            key: "events",
            title: "Events",
            component: EventsIndexPage,
            display: permission('view_sample_events'),
            isDefault: false,
            disabled: false,
        },
        {
            key: "files",
            title: "Files",
            component: FilesIndexPage,
            display: permission('view_files_tool'),
            isDefault: false,
            disabled: false,
        },
        {
            key: "permit",
            title: "Permits",
            component: PermitsIndexPage,
            display: permission('view_survey') && permission('view_permits'),
            isDefault: false,
            disabled: false,
        },
        {
            key: "photos",
            title: "Photos",
            component: PhotoComparisonPage,
            display: permission('view_photos'),
            isDefault: false,
            disabled: false,
        },
        {
            key: "activity",
            title: "Activity",
            component: ContextNodeActivityPage,
            display: permission('view_project_activity_feed_events'),
            isDefault: false,
            disabled: false,
        }
    ];

    // unsaved models only have the summary tool
    if(slug === 'new') {
        return [ret[0]]
    }

    return ret;
};

const getConActTools = (slug) => {
    const permission = usePermission();

    let ret = [
        {
            key: "summary",
            title: "Summary",
            component: ConservationActionsViewPage,
            display: true,
            isDefault: true,
            disabled: false,
        },
        {
            key: "eligibility",
            title: "Eligibility",
            component: EligibilityPage,
            display: true,
            isDefault: false,
            disabled: true,
        },
        {
            key: "design",
            title: "Design",
            component: DesignPage,
            display: true,
            isDefault: false,
            disabled: true,
        },
        {
            key: "permit",
            title: "Permits",
            component: PermitsPage,
            display: permission('view_permits'),
            isDefault: false,
            disabled: true,
        },
        {
            key: "as-built",
            title: "As Built",
            component: AsBuiltPage,
            display: true,
            isDefault: false,
            disabled: true,
        },
        {
            key: "events",
            title: "Events",
            component: EventsIndexPage,
            display: permission('view_sample_events'),
            isDefault: false,
            disabled: false,
        },
        {
            key: "files",
            title: "Files",
            component: FilesIndexPage,
            display: permission('view_files_tool'),
            isDefault: false,
            disabled: false,
        },
        {
            key: "activity",
            title: "Activity",
            component: ContextNodeActivityPage,
            display: permission('view_conservation_action_activity_feed_events'),
            isDefault: false,
            disabled: false,
        }
    ];

    // unsaved models only have the summary tool
    if(slug === 'new') {
        return [ret[0]]
    }

    return ret;
};

const getSampleEventTools = (slug) => {
    const permission = usePermission();

    let ret = [
        {
            key: "summary",
            title: "Summary",
            component: SampleEventViewPage,
            display: true,
            isDefault: true,
            disabled: false,
        },
        // {
        //     key: "activity",
        //     title: "Activity",
        //     component: ContextNodeActivityPage,
        //     display: permission('view_sample_event_activity_feed_events'),
        //     isDefault: false,
        //     disabled: false,
        // },
    ];

    // unsaved models only have the summary tool
    if(slug === 'new') {
        return [ret[0]]
    }

    return ret;
};
